"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";

export type ConsentStatus = "pending" | "accepted" | "rejected";

interface ConsentPreferences {
	analytics: boolean; // Existing Vercel Analytics
	posthog: boolean; // New PostHog tracking
	sessionRecording: boolean; // PostHog session recording
	experiments: boolean; // A/B testing participation
	necessary: boolean; // Always true
	timestamp: string;
	version: string;
}

interface ConsentStore {
	consentStatus: ConsentStatus;
	preferences: ConsentPreferences | null;
	isEUUser: boolean | null;
	isCCPAUser: boolean | null;
	hasInteracted: boolean;

	// Actions
	setConsentStatus: (status: ConsentStatus) => void;
	setPreferences: (preferences: ConsentPreferences) => void;
	setIsEUUser: (isEU: boolean) => void;
	setIsCCPAUser: (isCCPA: boolean) => void;
	setHasInteracted: (interacted: boolean) => void;
	acceptAll: () => void;
	rejectAll: () => void;
	updatePreferences: (
		analytics: boolean,
		posthog?: boolean,
		sessionRecording?: boolean,
		experiments?: boolean,
	) => void;
	shouldShowBanner: () => boolean;
	canTrack: () => boolean;
	canTrackPostHog: () => boolean;
	canRecordSessions: () => boolean;
	canParticipateInExperiments: () => boolean;
}

const CONSENT_VERSION = "2.0.0";

export const useConsentStore = create<ConsentStore>()(
	persist(
		(set, get) => ({
			consentStatus: "pending",
			preferences: null,
			isEUUser: null,
			isCCPAUser: null,
			hasInteracted: false,

			setConsentStatus: (status) => set({ consentStatus: status }),

			setPreferences: (preferences) =>
				set({
					preferences,
					consentStatus:
						preferences.analytics || preferences.posthog
							? "accepted"
							: "rejected",
					hasInteracted: true,
				}),

			setIsEUUser: (isEU) => set({ isEUUser: isEU }),

			setIsCCPAUser: (isCCPA) => set({ isCCPAUser: isCCPA }),

			setHasInteracted: (interacted) => set({ hasInteracted: interacted }),

			acceptAll: () => {
				const preferences: ConsentPreferences = {
					analytics: true,
					posthog: true,
					sessionRecording: true,
					experiments: true,
					necessary: true,
					timestamp: new Date().toISOString(),
					version: CONSENT_VERSION,
				};
				set({
					preferences,
					consentStatus: "accepted",
					hasInteracted: true,
				});
			},

			rejectAll: () => {
				const preferences: ConsentPreferences = {
					analytics: false,
					posthog: false,
					sessionRecording: false,
					experiments: false,
					necessary: true,
					timestamp: new Date().toISOString(),
					version: CONSENT_VERSION,
				};
				set({
					preferences,
					consentStatus: "rejected",
					hasInteracted: true,
				});
			},

			updatePreferences: (
				analytics,
				posthog = false,
				sessionRecording = false,
				experiments = false,
			) => {
				const preferences: ConsentPreferences = {
					analytics,
					posthog,
					sessionRecording,
					experiments,
					necessary: true,
					timestamp: new Date().toISOString(),
					version: CONSENT_VERSION,
				};
				set({
					preferences,
					consentStatus: analytics || posthog ? "accepted" : "rejected",
				});
			},

			shouldShowBanner: () => {
				const { isEUUser, hasInteracted, preferences } = get();

				// Don't show if we haven't determined location yet
				if (isEUUser === null) return false;

				// Don't show if user has already interacted
				if (hasInteracted && preferences) return false;

				// Show for EU users who haven't consented
				return isEUUser;
			},

			canTrack: () => {
				const { preferences, isEUUser, isCCPAUser } = get();

				// For non-EU, non-CCPA users, tracking is allowed by default
				if (!isEUUser && !isCCPAUser) return true;

				// For EU users, require explicit consent
				if (isEUUser) {
					return preferences?.analytics === true;
				}

				// For CCPA users, check if they've opted out
				if (isCCPAUser) {
					// If no preferences set, default to allowing tracking
					if (!preferences) return true;
					// Otherwise respect their choice
					return preferences.analytics !== false;
				}

				return true;
			},

			// Helper functions for specific consent checks
			canTrackPostHog: () => {
				const { preferences, isEUUser, isCCPAUser } = get();

				// Check for Do Not Track browser setting first
				if (checkDoNotTrack() || checkGlobalPrivacyControl()) {
					return false;
				}

				// For non-EU, non-CCPA users, PostHog tracking is allowed by default
				// This enables analytics for most users while respecting privacy laws
				if (isEUUser === false && isCCPAUser === false) {
					// If they explicitly opted out, respect that
					if (preferences?.posthog === false) return false;
					// Otherwise allow tracking
					return true;
				}

				// If we haven't determined location yet, default to allowing tracking
				// This prevents losing early session data
				if (isEUUser === null && isCCPAUser === null) {
					// But still respect explicit opt-out
					if (preferences?.posthog === false) return false;
					return true;
				}

				// For EU users, require explicit consent
				if (isEUUser) {
					return preferences?.posthog === true;
				}

				// For CCPA users, check if they've opted out
				if (isCCPAUser) {
					// If no preferences set, default to allowing tracking
					if (!preferences) return true;
					// Otherwise respect their choice
					return preferences.posthog !== false;
				}

				return true;
			},

			canRecordSessions: () => {
				const { preferences, isEUUser, isCCPAUser } = get();

				// For non-EU, non-CCPA users, session recording requires explicit consent
				if (!isEUUser && !isCCPAUser)
					return preferences?.sessionRecording === true;

				// For EU users, require explicit consent
				if (isEUUser) {
					return preferences?.sessionRecording === true;
				}

				// For CCPA users, check if they've opted out
				if (isCCPAUser) {
					return preferences?.sessionRecording === true;
				}

				return false;
			},

			canParticipateInExperiments: () => {
				const { preferences, isEUUser, isCCPAUser } = get();

				// For non-EU, non-CCPA users, experiments are allowed by default
				if (!isEUUser && !isCCPAUser) return preferences?.experiments !== false;

				// For EU users, require explicit consent
				if (isEUUser) {
					return preferences?.experiments === true;
				}

				// For CCPA users, check if they've opted out
				if (isCCPAUser) {
					return preferences?.experiments !== false;
				}

				return false;
			},
		}),
		{
			name: "analytics-consent",
			partialize: (state) => ({
				consentStatus: state.consentStatus,
				preferences: state.preferences,
				hasInteracted: state.hasInteracted,
			}),
		},
	),
);

// Utility function to check if user is from EU
export async function checkIfEUUser(): Promise<boolean> {
	try {
		// Check timezone for initial guess
		const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		const euTimezones = [
			"Europe/",
			"Africa/Ceuta",
			"Africa/Canary",
			"Atlantic/Canary",
			"Atlantic/Madeira",
			"Atlantic/Azores",
		];

		const isLikelyEU = euTimezones.some((tz) => timezone.startsWith(tz));

		// For production, you might want to use a geo-IP service
		// For now, we'll use timezone as a simple check
		return isLikelyEU;
	} catch {
		// Default to NOT showing banner if we can't determine
		// This allows tracking for most users while being cautious for EU
		return false;
	}
}

// Utility function to check if user is from California (CCPA)
export async function checkIfCCPAUser(): Promise<boolean> {
	try {
		const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		return (
			timezone === "America/Los_Angeles" || timezone === "America/San_Francisco"
		);
	} catch {
		return false;
	}
}

// Check for Do Not Track header
export function checkDoNotTrack(): boolean {
	if (typeof window === "undefined") return false;

	const dnt =
		navigator.doNotTrack ||
		(window as unknown as { doNotTrack?: string }).doNotTrack ||
		(navigator as unknown as { msDoNotTrack?: string }).msDoNotTrack;
	return dnt === "1" || dnt === "yes";
}

// Check for Global Privacy Control
export function checkGlobalPrivacyControl(): boolean {
	if (typeof window === "undefined") return false;

	return (
		(navigator as unknown as { globalPrivacyControl?: boolean })
			.globalPrivacyControl === true
	);
}
